import React from "react";
import { motion } from "framer-motion";
import { ArrowRight, ArrowUpRight } from "lucide-react";
import { useLang } from "../../i18n/LanguageContext";

const HERO_BG =
  "https://static.prod-images.emergentagent.com/jobs/6b5fd0ba-43ba-455a-9a6c-0c807b2b8e57/images/9764560347744696603029ce03857d91f29d1503b4145b5a18f29f01c62914df.png";

const fadeUp = {
  hidden: { opacity: 0, y: 24 },
  show: (i = 0) => ({
    opacity: 1,
    y: 0,
    transition: { duration: 0.8, delay: i * 0.12, ease: [0.22, 1, 0.36, 1] },
  }),
};

const Hero = () => {
  const { t } = useLang();

  const scrollTo = (id) => {
    const el = document.getElementById(id);
    if (el) {
      const y = el.getBoundingClientRect().top + window.scrollY - 80;
      window.scrollTo({ top: y, behavior: "smooth" });
    }
  };

  return (
    <section id="hero" className="relative pt-[140px] pb-24 lg:pt-[180px] lg:pb-32 overflow-hidden" data-testid="hero-section">
      <div
        aria-hidden
        className="absolute inset-0 pointer-events-none opacity-[0.35]"
        style={{
          backgroundImage: `url(${HERO_BG})`,
          backgroundSize: "cover",
          backgroundPosition: "right center",
          maskImage:
            "linear-gradient(to left, rgba(0,0,0,0.55) 0%, rgba(0,0,0,0) 70%)",
          WebkitMaskImage:
            "linear-gradient(to left, rgba(0,0,0,0.55) 0%, rgba(0,0,0,0) 70%)",
        }}
      />
      <div className="absolute inset-0 pointer-events-none bg-gradient-to-r from-[var(--ivory)] via-[var(--ivory)]/85 to-transparent" />

      <div className="relative max-w-[1280px] mx-auto px-6 lg:px-10 grid lg:grid-cols-12 gap-12 items-center">
        <div className="lg:col-span-8">
          <motion.div initial="hidden" animate="show" variants={fadeUp} custom={0}>
            <span className="overline" data-testid="hero-overline">{t.hero.overline}</span>
          </motion.div>

          <motion.h1
            initial="hidden"
            animate="show"
            variants={fadeUp}
            custom={1}
            className="font-serif text-[40px] sm:text-[56px] lg:text-[72px] leading-[1.04] tracking-tight text-[var(--navy)] mt-6 max-w-4xl"
            data-testid="hero-title"
          >
            {t.hero.title}
          </motion.h1>

          <motion.p
            initial="hidden"
            animate="show"
            variants={fadeUp}
            custom={2}
            className="mt-8 text-[17px] lg:text-[18px] leading-[1.7] text-[var(--charcoal)] max-w-2xl"
            data-testid="hero-subtitle"
          >
            {t.hero.subtitle}
          </motion.p>

          <motion.div
            initial="hidden"
            animate="show"
            variants={fadeUp}
            custom={3}
            className="mt-10 flex items-center gap-5"
          >
            <span className="divider-line" />
            <span className="font-serif italic text-[20px] lg:text-[22px] text-[var(--navy)]" data-testid="hero-slogan">
              {t.hero.slogan}
            </span>
          </motion.div>

          <motion.div
            initial="hidden"
            animate="show"
            variants={fadeUp}
            custom={4}
            className="mt-12 flex flex-col sm:flex-row gap-4"
          >
            <button className="btn-primary" onClick={() => scrollTo("contact")} data-testid="hero-cta-primary">
              {t.hero.ctaPrimary}
              <ArrowRight className="w-4 h-4" strokeWidth={1.5} />
            </button>
            <button className="btn-outline" onClick={() => scrollTo("services")} data-testid="hero-cta-secondary">
              {t.hero.ctaSecondary}
              <ArrowUpRight className="w-4 h-4" strokeWidth={1.5} />
            </button>
          </motion.div>
        </div>

        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 1, delay: 0.3 }}
          className="hidden lg:flex lg:col-span-4 items-center justify-center"
        >
          <div className="relative w-full max-w-[340px] aspect-square">
            <div className="absolute inset-0 border border-[var(--border-light)]" />
            <div className="absolute inset-3 bg-white flex items-center justify-center">
              <div className="text-center px-8">
                <p className="overline">EST. 2026</p>
                <p className="font-serif text-[32px] leading-[1.1] mt-6 text-[var(--navy)]">
                  ADR <span className="text-[var(--champagne)]">4</span><br />Solutions
                </p>
                <div className="mt-6 w-12 h-px bg-[var(--champagne)] mx-auto" />
                <p className="mt-6 text-xs tracking-[0.2em] uppercase text-[var(--charcoal)]">
                  IT Consulting<br/>Boutique
                </p>
              </div>
            </div>
            <div className="absolute -bottom-3 -right-3 w-24 h-24 border border-[var(--champagne)]" aria-hidden />
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default Hero;
